import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { categoriesAPI, extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';

const Categories = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  // Helper functions
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const renderIcon = (iconName) => {
    const icons = {
      folder: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      ),
      folderCheck: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          <polyline points="9 11 12 14 16 10" />
        </svg>
      ),
      folderX: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          <line x1="10" y1="11" x2="14" y2="15" />
          <line x1="14" y1="11" x2="10" y2="15" />
        </svg>
      ),
      grid: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      ),
      edit: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      ),
      trash: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
      ),
      folders: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      ),
    };
    return icons[iconName] || null;
  };

  // Calculate stats
  const stats = {
    total: categories.length,
    active: categories.filter(c => c.is_active).length,
    inactive: categories.filter(c => c.is_active === false).length,
    withDescription: categories.filter(c => c.description).length,
  };

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await categoriesAPI.list(selectedStore.id);
      setCategories(response.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load categories'));
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStore) loadCategories();
  }, [selectedStore, loadCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      if (editingId) {
        await categoriesAPI.update(editingId, formData);
        showToast('Category updated successfully', 'success');
      } else {
        await categoriesAPI.create({ ...formData, store_id: selectedStore.id });
        showToast('Category created successfully', 'success');
      }
      loadCategories();
      resetForm();
    } catch (err) {
      setError(extractErrorMessage(err, 'Operation failed'));
    }
  };

  const handleEdit = (category) => {
    setFormData({ name: category.name, description: category.description || '' });
    setEditingId(category.id);
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category? Products in this category will need to be reassigned.')) return;
    try {
      await categoriesAPI.delete(id);
      showToast('Category deleted', 'success');
      loadCategories();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete category'));
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '' });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading categories...</span></div>;
  }

  return (
    <div className="page-modern">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Categories</h1>
          <p className="page-subtitle">Organize your products into categories for better management</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => { setShowForm(!showForm); setError(null); }}
        >
          {showForm ? 'Cancel' : '+ Add Category'}
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Stats Row */}
      <div className="page-stats-row">
        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--primary)' }}>
            {renderIcon('folder')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Total Categories</div>
            <div className="page-stat-value">{formatNumber(stats.total)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--success)' }}>
            {renderIcon('folderCheck')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Active</div>
            <div className="page-stat-value">{formatNumber(stats.active)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--danger)' }}>
            {renderIcon('folderX')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Inactive</div>
            <div className="page-stat-value">{formatNumber(stats.inactive)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: '#8B5CF6' }}>
            {renderIcon('grid')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">With Description</div>
            <div className="page-stat-value">{formatNumber(stats.withDescription)}</div>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">{editingId ? 'Edit Category' : 'Add New Category'}</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Category Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Wedding Dresses, Jewelry, Accessories"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this category"
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingId ? 'Update Category' : 'Create Category'}
                </button>
                {editingId && (
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Data Table Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">All Categories ({categories.length})</h3>
          <div className="card-header-actions">
            <button className="btn btn-sm btn-secondary">Export</button>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {categories.length === 0 ? (
            <div className="page-empty-state">
              <div className="page-empty-icon">
                {renderIcon('folders')}
              </div>
              <div className="page-empty-title">No categories yet</div>
              <p className="page-empty-text">
                Create categories to organize your products and make them easier to find
              </p>
              <button
                className="btn btn-primary"
                style={{ marginTop: '1.5rem' }}
                onClick={() => setShowForm(true)}
              >
                Create Your First Category
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container hidden-mobile">
                <table>
                  <thead>
                    <tr>
                      <th>Category Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th style={{ width: '100px', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr key={category.id}>
                        <td className="font-medium">{category.name}</td>
                        <td className="text-muted">{category.description || '-'}</td>
                        <td>
                          <span className={`badge ${category.is_active ? 'badge-success' : 'badge-danger'}`}>
                            {category.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="table-action-btn"
                              onClick={() => handleEdit(category)}
                              title="Edit category"
                            >
                              {renderIcon('edit')}
                            </button>
                            <button
                              className="table-action-btn table-action-btn-danger"
                              onClick={() => handleDelete(category.id)}
                              title="Delete category"
                            >
                              {renderIcon('trash')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards hidden-desktop">
                {categories.map((category) => (
                  <div className="mobile-card" key={category.id}>
                    <div className="mobile-card-header">
                      <div className="mobile-card-title">{category.name}</div>
                      <span className={`badge ${category.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {category.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {category.description && (
                      <div className="mobile-card-row">
                        <span className="text-muted">{category.description}</span>
                      </div>
                    )}
                    <div className="mobile-card-actions">
                      <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(category)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(category.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Categories;
